import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import { CheckCircle, Circle, PencilSimple, Eye, FilePdf, Spinner, X, Image as ImageIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DocumentUploadSection } from '../components/DocumentUploadSection';
import { useAuth } from '../contexts/AuthContext';

export default function APQPDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [editingRemarks, setEditingRemarks] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const { user } = useAuth();

  // Check if user has full access (can edit/delete/create)
  // Admin and QA Manager always have full access
  // Other users have edit access if access_level === 'edit'
  const hasFullAccess = user?.role === 'admin' || user?.role === 'qa_manager' || user?.access_level === 'edit';

  useEffect(() => {
    loadProject();
    loadDocuments();
  }, [id]);

  const loadProject = async () => {
    try {
      const response = await api.get(`/apqp/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      // Load ALL documents related to this project (all categories)
      const response = await api.get(`/documents?related_id=${id}`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents');
    }
  };

  const handleDocToggle = (docId) => {
    setSelectedDocs(prev => {
      if (prev.includes(docId)) {
        return prev.filter(id => id !== docId);
      } else {
        return [...prev, docId];
      }
    });
  };

  const handleSelectAllDocs = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map(d => d.id));
    }
  };

  // Preview functions
  const handlePreview = async (doc) => {
    if (!doc.content_type?.startsWith('image/')) {
      toast.error('Preview only available for images');
      return;
    }
    
    setPreviewDoc(doc);
    setPreviewLoading(true);
    
    try {
      const response = await api.get(`/documents/${doc.id}/preview`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPreviewUrl(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const isImage = (doc) => doc.content_type?.startsWith('image/');

  const handleGenerateReport = async () => {
    setGeneratingPdf(true);
    try {
      const response = await api.post('/reports/generate-pdf', {
        report_type: 'apqp',
        project_id: id,
        document_ids: selectedDocs,
        include_images: true
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `APQP_Report_${project?.project_name?.replace(/\s+/g, '_') || id}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF report generated successfully');
      setShowReportModal(false);
    } catch (error) {
      toast.error('Failed to generate PDF report');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const updatePhase = async (phase) => {
    try {
      await api.patch(`/apqp/projects/${id}/phase?phase=${phase}`);
      toast.success(`Phase ${phase} completed`);
      loadProject();
    } catch (error) {
      toast.error('Failed to update phase');
    }
  };

  const handleCompletePhase = (phase) => {
    setSelectedPhase(phase);
    setShowRemarksModal(true);
    setEditingRemarks(false);
  };

  const handleEditRemarks = (phase) => {
    setSelectedPhase(phase);
    setShowRemarksModal(true);
    setEditingRemarks(true);
  };

  const handleSubmitRemarks = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const remarks = formData.get('remarks');
    
    try {
      await api.patch(`/apqp/projects/${id}/phase?phase=${selectedPhase}&remarks=${encodeURIComponent(remarks)}`);
      toast.success(editingRemarks ? 'Remarks updated successfully' : `Phase ${selectedPhase} completed`);
      setShowRemarksModal(false);
      setSelectedPhase(null);
      loadProject();
    } catch (error) {
      toast.error('Failed to update phase');
    }
  };

  const phases = [
    { num: 1, title: 'Plan & Define', desc: 'Define scope, team, and timing' },
    { num: 2, title: 'Product Design', desc: 'Design verification and validation' },
    { num: 3, title: 'Process Design', desc: 'Manufacturing process development' },
    { num: 4, title: 'Validation', desc: 'Production validation testing' },
    { num: 5, title: 'Feedback & Assessment', desc: 'Launch and continuous improvement' }
  ];

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading project...</div>;
  }

  if (!project) {
    return <div className="text-center py-12 text-gray-500">Project not found</div>;
  }

  return (
    <div data-testid="apqp-detail-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{project.project_name}</h1>
          <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
            <span><span className="font-medium">Customer:</span> {project.customer}</span>
            <span><span className="font-medium">Part:</span> {project.part_number}</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-sm ${
              project.status === 'active' ? 'bg-blue-100 text-blue-700' :
              project.status === 'completed' ? 'bg-green-100 text-green-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {project.status.toUpperCase()}
            </span>
          </div>
        </div>
        <button
          data-testid="generate-report-button"
          onClick={() => setShowReportModal(true)}
          className="flex items-center gap-2 bg-red-700 text-white px-4 py-2 rounded-sm hover:bg-red-800 transition-colors"
        >
          <FilePdf size={20} weight="bold" />
          Generate PDF Report
        </button>
      </div>

      <div className="space-y-4">
        {phases.map((phase) => {
          const isComplete = project[`phase_${phase.num}_complete`];
          const isCurrent = project.current_phase === phase.num;
          const phaseRemarks = project[`phase_${phase.num}_remarks`];
          
          return (
            <div
              key={phase.num}
              data-testid={`apqp-phase-${phase.num}`}
              className={`rounded-sm p-6 transition-all ${
                isComplete ? 'bg-green-50 border-2 border-green-600 shadow-sm' :
                isCurrent ? 'bg-blue-50 border-2 border-blue-600 shadow-md' :
                'bg-white border border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`mt-1 ${
                    isComplete ? 'text-green-600' : isCurrent ? 'text-blue-600' : 'text-gray-400'
                  }`}>
                    {isComplete ? (
                      <CheckCircle size={36} weight="fill" />
                    ) : (
                      <Circle size={36} weight={isCurrent ? 'bold' : 'regular'} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-lg font-bold ${
                      isComplete ? 'text-green-900' : isCurrent ? 'text-blue-900' : 'text-gray-900'
                    }`}>
                      Phase {phase.num}: {phase.title}
                    </h3>
                    <p className={`mt-1 text-sm ${
                      isComplete ? 'text-green-800' : isCurrent ? 'text-blue-800' : 'text-gray-600'
                    }`}>
                      {phase.desc}
                    </p>
                    
                    {isCurrent && !isComplete && (
                      <div className="mt-3 px-3 py-1 bg-blue-600 text-white text-xs uppercase tracking-wider font-bold rounded-sm inline-block">
                        ⚡ CURRENT PHASE - IN PROGRESS
                      </div>
                    )}
                    
                    {isComplete && (
                      <div className="mt-3">
                        <div className="px-3 py-1 bg-green-600 text-white text-xs uppercase tracking-wider font-bold rounded-sm inline-block mb-2">
                          ✓ COMPLETED
                        </div>
                        {phaseRemarks && (
                          <div className="mt-3 p-4 bg-white border border-green-300 rounded-sm">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-green-900 uppercase tracking-wider">Phase Remarks</p>
                              {hasFullAccess && (
                                <button
                                  data-testid={`edit-remarks-${phase.num}`}
                                  onClick={() => handleEditRemarks(phase.num)}
                                  className="flex items-center gap-1 text-green-700 hover:text-green-800 text-xs font-medium"
                                >
                                  <PencilSimple size={14} /> Edit
                                </button>
                              )}
                            </div>
                            <p className="text-sm text-gray-800">{phaseRemarks}</p>
                          </div>
                        )}
                        {!phaseRemarks && hasFullAccess && (
                          <button
                            data-testid={`add-remarks-${phase.num}`}
                            onClick={() => handleEditRemarks(phase.num)}
                            className="mt-2 text-xs text-green-700 hover:text-green-800 font-medium underline"
                          >
                            + Add Remarks
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {isCurrent && !isComplete && hasFullAccess && (
                  <button
                    data-testid={`complete-phase-${phase.num}-button`}
                    onClick={() => handleCompletePhase(phase.num)}
                    className="px-6 py-3 bg-gray-900 text-white text-sm font-bold rounded-sm hover:bg-gray-800 transition-colors shadow-sm"
                  >
                    Mark Complete
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!hasFullAccess && (
        <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 mt-6">
          <div className="flex items-center gap-2 text-amber-800">
            <Eye size={20} />
            <span className="font-medium">View-Only Access</span>
          </div>
          <p className="text-sm text-amber-700 mt-1">
            You have view-only access to this project. Editing, uploading, and downloading are restricted.
          </p>
        </div>
      )}

      <div className="mt-6">
        <DocumentUploadSection 
          category="apqp"
          relatedId={id}
          title="APQP Project Documents"
          viewOnly={!hasFullAccess}
        />
      </div>

      {showRemarksModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-xl">
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingRemarks ? 'Edit Phase Remarks' : `Complete Phase ${selectedPhase}`}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {phases.find(p => p.num === selectedPhase)?.title}
              </p>
            </div>
            <form onSubmit={handleSubmitRemarks} className="p-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  PHASE REMARKS / NOTES
                </label>
                <textarea
                  name="remarks"
                  data-testid="phase-remarks-input"
                  rows={6}
                  defaultValue={editingRemarks ? project[`phase_${selectedPhase}_remarks`] : ''}
                  placeholder="Enter key findings, decisions, issues, or notes for this phase..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                />
                <p className="mt-2 text-xs text-gray-500">
                  Document important information, decisions made, or issues encountered during this phase.
                </p>
              </div>
              <div className="flex gap-3 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowRemarksModal(false);
                    setSelectedPhase(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-phase-remarks"
                  className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 transition-colors font-medium"
                >
                  {editingRemarks ? 'Update Remarks' : 'Complete Phase'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PDF Report Generation Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="border-b border-gray-200 p-6 bg-red-700">
              <div className="flex items-center gap-3">
                <FilePdf size={28} weight="fill" className="text-white" />
                <div>
                  <h2 className="text-xl font-semibold text-white">Generate PDF Report</h2>
                  <p className="text-sm text-red-100 mt-1">
                    APQP Status Report for {project.project_name}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              {/* Report Preview Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-sm p-4 mb-6">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Report Contents</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600" weight="fill" />
                    Project information (Name, Customer, Part Number)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600" weight="fill" />
                    Phase status with completion percentage
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600" weight="fill" />
                    Phase remarks and notes
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600" weight="fill" />
                    Company branding (ALU-FIN logo)
                  </li>
                </ul>
              </div>

              {/* Document Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    Select Documents to Include
                  </h3>
                  {documents.length > 0 && (
                    <button
                      type="button"
                      onClick={handleSelectAllDocs}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {selectedDocs.length === documents.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>

                {documents.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-sm">
                    No documents attached to this project
                  </p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {documents.map((doc) => {
                      const docIsImage = isImage(doc);
                      return (
                        <div
                          key={doc.id}
                          className={`flex items-center gap-3 p-3 border rounded-sm transition-colors ${
                            selectedDocs.includes(doc.id)
                              ? 'bg-blue-50 border-blue-300'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedDocs.includes(doc.id)}
                            onChange={() => handleDocToggle(doc.id)}
                            className="w-4 h-4 text-blue-600 rounded cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {doc.original_filename}
                            </p>
                            <p className="text-xs text-gray-500">
                              <span className="uppercase font-medium">{doc.category}</span> • 
                              {docIsImage ? ' Image - will be previewed in PDF' : ` ${doc.content_type}`}
                            </p>
                          </div>
                          {docIsImage && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreview(doc);
                              }}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded-sm hover:bg-green-200 transition-colors"
                            >
                              <Eye size={14} /> Preview
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  All selected images (JPG/PNG) from all categories will be embedded as thumbnails in the PDF report.
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowReportModal(false);
                    setSelectedDocs([]);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="download-pdf-report"
                  onClick={handleGenerateReport}
                  disabled={generatingPdf}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-700 text-white rounded-sm hover:bg-red-800 transition-colors disabled:opacity-50"
                >
                  {generatingPdf ? (
                    <>
                      <Spinner size={18} className="animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FilePdf size={18} weight="bold" />
                      Download PDF Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60]" onClick={closePreview}>
          <div className="bg-white rounded-sm max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="border-b border-gray-200 p-4 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="font-semibold text-gray-900">{previewDoc.original_filename}</h3>
                <p className="text-xs text-gray-500">Preview Only</p>
              </div>
              <button
                onClick={closePreview}
                className="p-2 hover:bg-gray-200 rounded-sm transition-colors"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center bg-gray-100 min-h-[300px]">
              {previewLoading ? (
                <p className="text-gray-500">Loading preview...</p>
              ) : previewUrl ? (
                <img 
                  src={previewUrl} 
                  alt={previewDoc.original_filename}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              ) : (
                <p className="text-gray-500">Preview not available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
